import { AxiosRequestConfig } from 'axios'

import {
  DestinationTypes,
  GeneralFailResponse,
  SpecialServiceStatuses,
  SpecialServiceTypes,
} from 'common/types/commonTypes'
import { ObjectWithTranslation, ObjectWithTranslationFromBE } from 'common/types/transformedRespTypes'

import { callAPI, takamolAPIWithCredentials } from '../axios'

export type GetSpecialServicesListParams = {
  page?: number
  perPage?: number
  service?: string
  status?: string
}

export type SpecialServiceFromBE = {
  display_id: string
  name: string
  id_number: string
  service: SpecialServiceTypes
  created_at: string
  status: SpecialServiceStatuses
  relationship_manager_name: string
}

export type GetSpecialServicesListResponse = {
  data: Array<SpecialServiceFromBE>
  current_page: number
  last_page: number
}

const getSpecialServicesList = (
  { service, status, page = 0, perPage = 10 }: GetSpecialServicesListParams,
  config: AxiosRequestConfig,
) =>
  takamolAPIWithCredentials.get<GetSpecialServicesListResponse>('/api/individuals/special-services', {
    params: {
      service: service !== 'ALL' ? service : undefined,
      status,
      page: page + 1,
      per_page: perPage,
    },
    ...config,
  })

export type GetGovernmentAgenciesResponse = {
  data: Array<ObjectWithTranslationFromBE>
}

const getGovernmentAgencies = (params: {}, config: AxiosRequestConfig) =>
  takamolAPIWithCredentials.get<GetGovernmentAgenciesResponse>(
    '/api/individuals/government-agencies-followup/agencies',
    config,
  )

export type GetConsultancyTypesResponse = {
  data: Array<ObjectWithTranslationFromBE>
}

const getConsultancyTypes = (params: {}, config: AxiosRequestConfig) =>
  takamolAPIWithCredentials.get<GetConsultancyTypesResponse>('/api/individuals/consultancy-requests/types', config)

export type CreateGovernmentRequestParams = {
  idNumber: number
  agencyId?: string
  description: string
  agencyLabelAr?: string
  agencyLabelEn?: string
  files?: Array<string>
}

export type CreateRequestResponse = {
  data: {
    display_id: string
  }
}

const createGovernmentRequest = (
  { idNumber, agencyId, agencyLabelAr, agencyLabelEn, description, files }: CreateGovernmentRequestParams,
  config: AxiosRequestConfig,
) =>
  takamolAPIWithCredentials.post<CreateRequestResponse>(
    '/api/individuals/government-agencies-followup',
    {
      premium_user_id_number: idNumber,
      ...(agencyId === '0'
        ? {
            government_agency_label: agencyLabelAr,
            government_agency_label_en: agencyLabelEn,
          }
        : {
            government_agency_id: agencyId,
          }),
      description,
      ...(files?.length ? { files } : {}),
    },
    config,
  )

export type CreateConsultancyRequestParams = {
  idNumber: number
  consultancyTypeId: string
  description: string
  files?: Array<string>
}

const createConsultancyRequest = (
  { idNumber, consultancyTypeId, description, files }: CreateConsultancyRequestParams,
  config: AxiosRequestConfig,
) =>
  takamolAPIWithCredentials.post<CreateRequestResponse>(
    '/api/individuals/consultancy-requests',
    {
      premium_user_id_number: idNumber,
      consultancy_type_id: consultancyTypeId,
      inquiry: description,
      ...(files?.length ? { files } : {}),
    },
    config,
  )

export type CreateDomesticRequestParams = {
  idNumber: number
  destination: DestinationTypes
  description?: string
  files?: Array<string>
}

const createDomesticRequest = (
  { idNumber, destination, description, files }: CreateDomesticRequestParams,
  config: AxiosRequestConfig,
) =>
  takamolAPIWithCredentials.post<CreateRequestResponse>(
    '/api/individuals/arrival-requests',
    {
      premium_user_id_number: idNumber,
      destination,
      details: description,
      ...(files?.length ? { files } : {}),
    },
    config,
  )

export type UploadFileParams = {
  file: File
}

export type UploadFileResponse = {
  key: string
}

const fileToFormData = (file: File) => {
  const formData = new FormData()
  formData.append('file', file)

  return formData
}

const uploadFile = ({ file }: UploadFileParams, config: AxiosRequestConfig) =>
  takamolAPIWithCredentials.post<UploadFileResponse>(
    '/api/media/uploads/musaned-individuals',
    fileToFormData(file),
    config,
  )
export type GetSpecialServiceParams = {
  id: string
}

export type AttachmentFromBE = {
  file_name: string
  url: string
}

export type SpecialServiceDetailsFromBE = {
  comments?: {
    created_at: string
    files: AttachmentFromBE[]
    comment: string
    author: {
      name: string
    }
  }[]
  display_id: string
  destination?: string
  details?: string
  status: SpecialServiceStatuses
  description?: string
  inquiry?: string
  files: AttachmentFromBE[]
  government_agency?: ObjectWithTranslationFromBE
  consultancy_type?: ObjectWithTranslationFromBE
  premium_user: {
    name: string
    id_number: string
    mobile: string
    relationship_manager?: {
      name: string
      mobile: string
    }
  }
  completed_at?: string
  created_at?: string
}

export type Attachment = {
  fileName: string
  url: string
}

export type SpecialServiceDetails = {
  comments:
    | {
        createdAt: string
        files: Attachment[]
        comment: string
        author: {
          name: string
        }
      }[]
    | null
  displayId: string
  destination?: string
  details?: string
  status: SpecialServiceStatuses
  description?: string
  inquiry?: string
  files: Attachment[] | null
  governmentAgency: ObjectWithTranslation | null
  consultancyType: ObjectWithTranslation | null
  premiumUser: {
    name: string
    idNumber: string
    mobile: string
    relationshipManager: {
      name: string
      mobile: string
    } | null
  }
  completedAt?: string
  createdAt?: string
}

export type GetSpecialServiceDetailsResponse = {
  data: SpecialServiceDetailsFromBE
}

const getSpecialServiceRequest = ({ id }: GetSpecialServiceParams, config: AxiosRequestConfig) =>
  takamolAPIWithCredentials.get<GetSpecialServiceDetailsResponse>(`/api/individuals/special-services/${id}`, config)

export default {
  getSpecialServicesList: callAPI<typeof getSpecialServicesList, GetSpecialServicesListResponse, GeneralFailResponse>(
    getSpecialServicesList,
  ),
  getGovernmentAgencies: callAPI<typeof getGovernmentAgencies, GetGovernmentAgenciesResponse, GeneralFailResponse>(
    getGovernmentAgencies,
  ),
  getConsultancyTypes: callAPI<typeof getConsultancyTypes, GetConsultancyTypesResponse, GeneralFailResponse>(
    getConsultancyTypes,
  ),
  createGovernmentRequest: callAPI<typeof createGovernmentRequest, CreateRequestResponse, GeneralFailResponse>(
    createGovernmentRequest,
  ),
  createConsultancyRequest: callAPI<typeof createConsultancyRequest, CreateRequestResponse, GeneralFailResponse>(
    createConsultancyRequest,
  ),
  createDomesticRequest: callAPI<typeof createDomesticRequest, CreateRequestResponse, GeneralFailResponse>(
    createDomesticRequest,
  ),
  uploadFile: callAPI<typeof uploadFile, UploadFileResponse, GeneralFailResponse>(uploadFile),
  getSpecialServiceRequest: callAPI<
    typeof getSpecialServiceRequest,
    GetSpecialServiceDetailsResponse,
    GeneralFailResponse
  >(getSpecialServiceRequest),
}
